/**
 * @fileoverview method to do formatting of dates relative to a given date in a human-
 *   readable manner that meets our Braze-specific requirements for date display
 */

import { TimeUnit } from ".";
import type { I18nT } from "@lib/dashboardI18n";

/**
 * Returns a localized string given a translation function and date values of the
 * formatted date value.
 *
 * @note these use the number values rather than a Date object on account of
 *   serializability and shallow comparisons
 *
 * @param t function that translates messages
 * @param date the date value to be formatted, expressed as milliseconds since the unix
 *   epoch
 * @param relativeTo the relative date value, expressed as milliseconds since the unix
 *   epoch. defaults to `Date.now()`
 */
export const formatRelative = (
  t: I18nT<"relative-date-formats">,
  date: number,
  relativeTo = Date.now()
): string => {
  // normalized to seconds
  let difference = Math.floor((relativeTo - date) / 1000);

  if (difference < 0) {
    difference = Math.abs(difference);

    if (difference < TimeUnit.Minute) {
      return t("future-less-than-minute", "a few seconds from now");
    } else if (difference < TimeUnit.Hour) {
      return t("future-less-than-hour", "{ minutes, plural, =1 { 1 minute } other { # minutes } } from now", {
        minutes: Math.floor(difference / TimeUnit.Minute),
      });
    } else if (difference < TimeUnit.Day) {
      return t("future-less-than-day", "{ hours, plural, =1 { 1 hour } other { # hours } } from now", {
        hours: Math.floor(difference / TimeUnit.Hour),
      });
    } else if (difference < TimeUnit.Day * 31) {
      return t("future-less-than-31-days", "{ days, plural, =1 { 1 day } other { # days } } from now", {
        days: Math.floor(difference / TimeUnit.Day),
      });
    }
  } else if (difference === 0) {
    return t("now", "now");
  } else {
    if (difference < TimeUnit.Minute) {
      return t("less-than-minute", "a few seconds ago");
    } else if (difference < TimeUnit.Hour) {
      return t("less-than-hour", "{ minutes, plural, =1 { 1 minute } other { # minutes } } ago", {
        minutes: Math.floor(difference / TimeUnit.Minute),
      });
    } else if (difference < TimeUnit.Day) {
      return t("less-than-day", "{ hours, plural, =1 { 1 hour } other { # hours } } ago", {
        hours: Math.floor(difference / TimeUnit.Hour),
      });
    } else if (difference < TimeUnit.Day * 31) {
      return t("less-than-31-days", "{ days, plural, =1 { 1 day } other { # days } } ago", {
        days: Math.floor(difference / TimeUnit.Day),
      });
    }
  }

  // defer calling `new Date` if not handled above
  /**
   * @deprecated TODO: remove Date object and replace with new date/time utils. For details, see https://confluence.braze.com/display/ENG/Date+and+time+handling
   */
  const relativeYear = new Date(relativeTo);
  /**
   * @deprecated TODO: remove Date object and replace with new date/time utils. For details, see https://confluence.braze.com/display/ENG/Date+and+time+handling
   */
  const dateToDisplay = new Date(date);

  // if the date is in the same year as the relative date, omit it
  if (relativeYear.getFullYear() === dateToDisplay.getFullYear()) {
    return t("date-in-same-year", "{ date, date, ::MMM d }", { date: dateToDisplay });
  }
  return t("date-different-year", "{ date, date, ::MMM d y }", { date: dateToDisplay });
};
